/**
 * TenantDetailPage - Overview / Users / Projects / AI Models / Settings tabs for one tenant
 */

import { useState, useEffect, type ReactNode } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Bug, Users as UsersIcon, Bot } from 'lucide-react';
import { LoadingState, StatusBadge, ConfirmDialog, Toast } from '../components/common';
import { AgentModelsPanel } from '../components/tenants/AgentModelsPanel';
import { CodeReviewGraphPanel } from '../components/tenants/CodeReviewGraphPanel';
import { useTenantDetailPage, type TenantDetailTab } from '../hooks/useTenantDetailPage';

const TABS: TenantDetailTab[] = ['overview', 'users', 'projects', 'models', 'settings'];

const TAB_LABELS: Record<TenantDetailTab, string> = {
  overview: 'Overview',
  users: 'Users',
  projects: 'Projects',
  models: 'AI Models',
  settings: 'Settings',
};

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = (searchParams.get('tab') as TenantDetailTab) || 'overview';

  // Hooks must run unconditionally (rules-of-hooks) - fall back to an empty id
  // when the route somehow matched without one, and bail out in the render below.
  const {
    overview,
    isOverviewLoading,
    hasOverviewError,
    users,
    isUsersLoading,
    projects,
    isProjectsLoading,
    agentModels,
    isAgentModelsLoading,
    hasAgentModelsError,
    updateMutation,
    agentModelsMutation,
    toast,
  } = useTenantDetailPage(id ?? '', activeTab);

  const [editedName, setEditedName] = useState('');
  const [confirmDeactivate, setConfirmDeactivate] = useState(false);

  useEffect(() => {
    if (overview) setEditedName(overview.tenant.name);
  }, [overview]);

  if (!id) {
    return <p className="text-gray-600 dark:text-gray-400">Tenant not found</p>;
  }

  const handleTabChange = (tab: TenantDetailTab) => setSearchParams({ tab });

  if (hasOverviewError) {
    return (
      <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6">
        <p className="text-red-800 dark:text-red-200">Failed to load tenant</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <LoadingState isLoading={isOverviewLoading} message="Loading tenant..." />

      {overview && (
        <>
          {/* Header */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/tenants')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-xl transition-colors"
            >
              <ArrowLeft className="w-6 h-6 text-gray-600 dark:text-gray-400" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{overview.tenant.name}</h2>
              <p className="text-gray-600 dark:text-gray-400 mt-1">{overview.tenant.domain}</p>
            </div>
            <StatusBadge status={overview.tenant.status} size="lg" />
          </div>

          {/* Tabs */}
          <div className="tab-group">
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`tab ${activeTab === tab ? 'active' : ''}`}
              >
                {TAB_LABELS[tab]}
              </button>
            ))}
          </div>

          {/* Overview */}
          {activeTab === 'overview' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                icon={<CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />}
                iconBg="bg-green-100 dark:bg-green-900/30"
                label="Bug Resolution Rate"
                value={`${overview.summary.successRate}%`}
              />
              <MetricCard
                icon={<Bug className="w-6 h-6 text-indigo-600 dark:text-indigo-400" />}
                iconBg="bg-indigo-100 dark:bg-indigo-900/30"
                label="Total Bugs Fixed"
                value={overview.summary.tasksCompleted}
              />
              <MetricCard
                icon={<UsersIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />}
                iconBg="bg-purple-100 dark:bg-purple-900/30"
                label="Active Users"
                value={overview.tenant.userCount}
              />
              <MetricCard
                icon={<Bot className="w-6 h-6 text-orange-600 dark:text-orange-400" />}
                iconBg="bg-orange-100 dark:bg-orange-900/30"
                label="Total Bot Runs"
                value={overview.summary.totalTasks}
              />
            </div>
          )}

          {/* Users */}
          {activeTab === 'users' && (
            <div className="space-y-6">
              <LoadingState isLoading={isUsersLoading} message="Loading users..." />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Users for {overview.tenant.name}
              </p>
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Name</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {users.length === 0 && !isUsersLoading && (
                        <tr>
                          <td colSpan={4} className="px-6 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                            No users found
                          </td>
                        </tr>
                      )}
                      {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                          <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{user.name ?? '—'}</td>
                          <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">{user.email}</td>
                          <td className="px-6 py-4">
                            <StatusBadge status={user.role} showDot={false} />
                          </td>
                          <td className="px-6 py-4">
                            <StatusBadge status={user.status} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Projects */}
          {activeTab === 'projects' && (
            <div className="space-y-6">
              <LoadingState isLoading={isProjectsLoading} message="Loading projects..." />
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Projects for {overview.tenant.name}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {projects.length === 0 && !isProjectsLoading && (
                  <p className="text-sm text-gray-500 dark:text-gray-400">No projects found</p>
                )}
                {projects.map((project) => (
                  <div
                    key={project.id}
                    className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900 dark:text-white">{project.name}</h4>
                        {project.description && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{project.description}</p>
                        )}
                      </div>
                      <StatusBadge status={project.status} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Models */}
          {activeTab === 'models' && (
            <AgentModelsPanel
              agentModels={agentModels}
              isLoading={isAgentModelsLoading}
              hasError={hasAgentModelsError}
              isSaving={agentModelsMutation.isPending}
              onSave={(models) => agentModelsMutation.mutate(models)}
            />
          )}

          {/* Settings */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">General Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Tenant Name</label>
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400 transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Domain</label>
                    <input
                      type="text"
                      value={overview.tenant.domain}
                      disabled
                      className="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700/50 border border-gray-300 dark:border-gray-600 rounded-xl text-gray-500 dark:text-gray-400 cursor-not-allowed"
                    />
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => updateMutation.mutate({ name: editedName })}
                      disabled={updateMutation.isPending || editedName === overview.tenant.name}
                      className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium transition-all disabled:opacity-50"
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditedName(overview.tenant.name)}
                      className="px-4 py-2 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>

              <CodeReviewGraphPanel
                enabled={overview.tenant.codeReviewGraphEnabled}
                pendingUpdate={updateMutation.variables}
                isSaving={updateMutation.isPending}
                onChange={(enabled) => updateMutation.mutate({ codeReviewGraphEnabled: enabled })}
              />

              <div className="bg-white dark:bg-gray-800 rounded-xl shadow border-2 border-red-200 dark:border-red-900/50 p-6">
                <h3 className="text-lg font-semibold text-red-600 dark:text-red-400">Danger Zone</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 mb-4">Irreversible and destructive actions</p>
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-200 dark:border-red-900/50">
                  <h4 className="font-medium text-gray-900 dark:text-white mb-2">
                    {overview.tenant.status === 'active' ? 'Deactivate Tenant' : 'Reactivate Tenant'}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                    {overview.tenant.status === 'active'
                      ? 'This will suspend all bot operations and disable user access. This action can be reversed later.'
                      : 'This will restore bot operations and user access for this tenant.'}
                  </p>
                  <button
                    onClick={() => setConfirmDeactivate(true)}
                    className={`px-4 py-2 text-white rounded-xl font-medium transition-all ${
                      overview.tenant.status === 'active' ? 'bg-red-600 hover:bg-red-700' : 'bg-indigo-600 hover:bg-indigo-700'
                    }`}
                  >
                    {overview.tenant.status === 'active' ? 'Deactivate Tenant' : 'Reactivate Tenant'}
                  </button>
                </div>
              </div>

              <ConfirmDialog
                isOpen={confirmDeactivate}
                onClose={() => setConfirmDeactivate(false)}
                onConfirm={() =>
                  updateMutation.mutate({ status: overview.tenant.status === 'active' ? 'inactive' : 'active' })
                }
                title={overview.tenant.status === 'active' ? 'Deactivate Tenant' : 'Reactivate Tenant'}
                message={`Are you sure you want to ${overview.tenant.status === 'active' ? 'deactivate' : 'reactivate'} "${overview.tenant.name}"?`}
                confirmLabel={overview.tenant.status === 'active' ? 'Deactivate' : 'Reactivate'}
                variant={overview.tenant.status === 'active' ? 'danger' : 'info'}
              />
            </div>
          )}
        </>
      )}

      <Toast isOpen={toast.isOpen} onClose={toast.close} message={toast.message} variant={toast.variant} />
    </div>
  );
}

function MetricCard({
  icon,
  iconBg,
  label,
  value,
}: {
  icon: ReactNode;
  iconBg: string;
  label: string;
  value: string | number;
}) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-6 border border-gray-200 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className={`p-3 rounded-xl ${iconBg}`}>{icon}</div>
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{label}</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}
